/**
 * usePermissions
 *
 * Fetches the signed-in user's permission list from
 * GET /manage/my-permissions and caches it for the session via useState
 * (Nuxt's shared, SSR-safe reactive state -- persists across route
 * navigations within the same client session, so this only fetches once
 * per session rather than on every page load).
 *
 * Replaces hardcoded role checks like `role.value === 'admin'` with
 * `hasPermission('admin.access')` -- what a role can do is now defined by
 * the role_permissions table on the Worker, not by name-matching in
 * frontend code.
 *
 * Note: cached for the session. If a role changes mid-session (rare --
 * e.g. an admin edits Clerk directly while already signed in elsewhere),
 * this won't pick it up until the next full page load/sign-in, same
 * limitation the rest of the app already has around live role changes.
 */
export function usePermissions() {
  const permissions = useState<string[] | null>('user-permissions', () => null)
  const permRole     = useState<string | null>('user-permissions-role', () => null)
  const permClerkId  = useState<string | null>('user-permissions-clerk-id', () => null)
  const loading       = useState<boolean>('user-permissions-loading', () => false)

  // Called once, synchronously, right here -- NOT re-invoked inside
  // fetchPermissions() below. That was the real bug: calling
  // useClerkUser() (which wraps Clerk's own useUser()) deep inside an
  // already-async function, after several await boundaries had already
  // passed (the isLoaded wait in the middleware, then this function's
  // own awaits), loses Vue's composable context. Confirmed live via the
  // actual thrown error: "@clerk/vue: useUser can only be used when the
  // Vue plugin is installed" -- not a permissions-logic bug at all, a
  // Vue-context bug from calling a composable in the wrong place.
  const { getToken, user } = useClerkUser()

  async function fetchPermissions() {
    loading.value = true
    try {
      const { apiBase: API } = useRuntimeConfig().public
      const token = await getToken()
      if (!token) {
        console.error('[usePermissions] getToken() returned empty/falsy -- no valid Clerk session token available')
        permissions.value = []
        permRole.value = null
        permClerkId.value = null
        return
      }
      const res = await fetch(`${API}/manage/my-permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const body = await res.text().catch(() => '(could not read response body)')
        console.error(`[usePermissions] /manage/my-permissions returned ${res.status}:`, body)
        permissions.value = []
        permRole.value = null
        permClerkId.value = null
        return
      }
      const data = await res.json()
      permissions.value = Array.isArray(data.permissions) ? data.permissions : []
      permRole.value = data.role ?? null
      permClerkId.value = user.value?.id ?? null
    } catch (e) {
      console.error('[usePermissions] fetchPermissions() threw:', e)
      permissions.value = []
      permRole.value = null
      permClerkId.value = null
    } finally {
      loading.value = false
    }
  }

  /**
   * Ensures the cached permissions actually belong to the currently
   * signed-in user before relying on them. Previously the middleware
   * only checked `permissions.value === null` -- skipping a refetch
   * whenever anything was already cached, regardless of who it was
   * cached for. resetPermissions() was only ever called from the
   * explicit Sign Out button, so any account switch that didn't go
   * through that exact click (Clerk's own session switcher, browser
   * quirks, testing multiple accounts in one session) could leave a
   * DIFFERENT user's real access level -- including an empty one from
   * testing a lower-privileged account -- silently applied to whoever's
   * actually signed in now. Confirmed live: a genuine admin denied
   * access after a prior test session as an archobbyist account.
   */
  async function ensureFreshPermissions(currentClerkId: string | null | undefined) {
    if (permissions.value === null || permClerkId.value !== (currentClerkId ?? null)) {
      permissions.value = null
      permRole.value = null
      permClerkId.value = null
      await fetchPermissions()
    }
  }

  function hasPermission(key: string): boolean {
    return permissions.value?.includes(key) ?? false
  }

  /** Clears the cache -- call on sign-out so a new session starts fresh. */
  function resetPermissions() {
    permissions.value = null
    permRole.value = null
    permClerkId.value = null
  }

  return { permissions, permRole, loading, fetchPermissions, hasPermission, resetPermissions, ensureFreshPermissions }
}
